<template>
  <v-dialog v-model="dialog" persistent max-width="600px" overlay-opacity="0.85" content-class="modern-links-dialog">
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title class="editor-header d-flex justify-space-between align-center px-6 py-4">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-link-variant</v-icon>
          Edit Social Links
        </div>
        <v-btn icon @click="onClose" class="close-btn" color="white" depressed large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Update your agency's website and social media links
        </div>
      </v-card-text>

      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert :type="messageType" class="mb-4 elevation-2" transition="scale-transition"
            :icon="messageType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'">
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Links Form -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <v-col cols="12" class="py-2">
            <v-text-field v-model="profile.website" label="Website URL" outlined dense prepend-inner-icon="mdi-web"
              :rules="[rules.website]" @input="onChange" @blur="onUrlBlur('website')" class="modern-input"
              placeholder="example.com or https://example.com"
              hint="Enter your website URL (https: or http:// will be added automatically)" persistent-hint />
          </v-col>
          <v-col cols="12" class="py-2">
            <v-text-field v-model="profile.facebook" label="Facebook" outlined dense prepend-inner-icon="mdi-facebook"
              :rules="[rules.social]" @input="onChange" @blur="onUrlBlur('facebook')" class="modern-input"
              placeholder="yourpage or facebook.com/yourpage"
              hint="Enter your Facebook page name or URL (https:// will be added automatically)" persistent-hint />
          </v-col>
          <v-col cols="12" class="py-2">
            <v-text-field v-model="profile.twitter" label="Twitter" outlined dense prepend-inner-icon="mdi-twitter"
              :rules="[rules.social]" @input="onChange" @blur="onUrlBlur('twitter')" class="modern-input"
              placeholder="yourhandle or twitter.com/yourhandle"
              hint="Enter your Twitter handle or URL (https:// will be added automatically)" persistent-hint />
          </v-col>
          <!-- <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.linkedin"
              label="LinkedIn"
              outlined
              dense
              prepend-inner-icon="mdi-linkedin"
              :rules="[rules.social]"
              @input="onChange"
              class="modern-input"
              placeholder="https://linkedin.com/company/yourpage"
            />
          </v-col> -->
          <!-- <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.instagram"
              label="Instagram"
              outlined
              dense
              prepend-inner-icon="mdi-instagram"
              :rules="[rules.social]"
              @input="onChange"
              class="modern-input"
              placeholder="https://instagram.com/yourhandle"
            />
          </v-col> -->
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn color="primary" @click="onSave" depressed width="160" :loading="loading" :disabled="!profile.website"
          class="px-6 save-btn" large dark>
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Links
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapActions, mapState } from "vuex";
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      messageType: 'error',
      rules: {
        required: value => !!value || "Required.",
        website: value => {
          if (!value) return true; // Allow empty for optional fields

          // Automatically format URL if missing protocol
          if (value && !value.match(/^https?:\/\//)) {
            return "Please include https:// or http:// at the beginning";
          }

          // Basic URL validation - fixed escape characters
          const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
          return urlPattern.test(value) || "Please enter a valid URL (e.g., https://example.com)";
        },
        social: value => {
          if (!value) return true; // Allow empty for optional fields

          // For social media, we don't require https:// prefix - just validate the content
          // Remove any protocol if present for validation
          const cleanValue = value.replace(/^https?:\/\//, '');

          // Allow various formats: username, platform.com/username, or full URL
          const socialPattern = /^(www\.)?(facebook|twitter|instagram|linkedin)\.com\/[\w.-]+$|^[\w.-]+$/;
          return socialPattern.test(cleanValue) || "Please enter a valid social media URL or username";
        }
      },
      profile: {}
    };
  },
  methods: {
    ...mapActions("auth", {
      updateProfile: "updateProfile"
    }),
    // Add URL formatting method
    formatUrl(url) {
      if (!url) return url;

      // Remove leading/trailing whitespace
      url = url.trim();

      // If URL doesn't start with http:// or https://, add https://
      if (!url.match(/^https?:\/\//)) {
        url = 'https://' + url;
      }

      return url;
    },
    // Add specialized formatting for social media URLs
    formatSocialUrl(url, platform) {
      if (!url) return url;

      // Remove leading/trailing whitespace
      url = url.trim();

      // If it already has a protocol, return as is
      if (url.match(/^https?:\/\//)) {
        return url;
      }

      // Platform base URLs
      const platformBases = {
        facebook: 'https://facebook.com/',
        twitter: 'https://twitter.com/'
      };

      // If it starts with the platform domain, add protocol
      if (url.startsWith(`${platform}.com`)) {
        return `https://${url}`;
      }

      // If it's just a username (no domain), add the platform base
      if (!url.includes('.com') && platformBases[platform]) {
        // Remove any leading @ or / characters
        const cleanUsername = url.replace(/^[@/]+/, '');
        return platformBases[platform] + cleanUsername;
      }

      // Fallback to generic URL formatting
      return this.formatUrl(url);
    },
    // Update the blur handler for social media fields
    onUrlBlur(field) {
      if (this.profile[field]) {
        if (field === 'facebook' || field === 'twitter') {
          this.profile[field] = this.formatSocialUrl(this.profile[field], field);
        } else {
          this.profile[field] = this.formatUrl(this.profile[field]);
        }
      }
    },

    onChange() {
      this.message = null;
    },


    onSave() {
      // Validate and format URLs before saving
      if (!this.profile.website) {
        this.message = "Agency website is required";
        this.messageType = 'error';
        return;
      }

      // Format all URLs before saving with appropriate methods
      const formattedProfile = {
        website: this.formatUrl(this.profile.website),
        facebook: this.formatSocialUrl(this.profile.facebook, 'facebook'),
        twitter: this.formatSocialUrl(this.profile.twitter, 'twitter')
      };

      // Validate formatted URLs
      const websiteValid = this.rules.website(formattedProfile.website);
      const facebookValid = this.rules.social(formattedProfile.facebook);
      const twitterValid = this.rules.social(formattedProfile.twitter);

      if (websiteValid !== true) {
        this.message = websiteValid;
        this.messageType = 'error';
        return;
      }

      if (formattedProfile.facebook && facebookValid !== true) {
        this.message = facebookValid;
        this.messageType = 'error';
        return;
      }

      if (formattedProfile.twitter && twitterValid !== true) {
        this.message = twitterValid;
        this.messageType = 'error';
        return;
      }

      this.loading = true;
      this.updateProfile(formattedProfile)
        .then(() => {
          this.loading = false;
          this.message = "Links updated successfully!";
          this.messageType = 'success';

          // Update local profile with formatted URLs
          this.profile = { ...this.profile, ...formattedProfile };

          // Auto-close after success
          setTimeout(() => {
            this.onClose();
          }, 1500);
        })
        .catch(error => {
          this.loading = false;
          this.messageType = 'error';
          if (error.response) {
            this.message = error.response.data.message;
          } else {
            this.message = error;
          }
        });
    }
  },
  computed: {
    ...mapState("auth", {
      myProfile: "profile"
    })
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
  }
};
</script>

<style scoped>
.modern-links-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-links-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-links-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-links-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-links-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}

/* Platform-specific icon colors */
.modern-input::v-deep .mdi-facebook {
  color: #1877F2 !important;
}

.modern-input::v-deep .mdi-twitter {
  color: #1DA1F2 !important;
}

.modern-input::v-deep .mdi-linkedin {
  color: #0077B5 !important;
}

.modern-input::v-deep .mdi-instagram {
  color: #E1306C !important;
}
</style>